// S7 · Step 4 — Opening state (`/onboarding/opening`, Module 02 §4 S7, §3.5).
//
// WHY THIS SCREEN EXISTS AT ALL, given Modules 06/09/10 are unwired: without it
// `onboarding_step_states[4]` could never reach `completed`, so the session could
// never reach `ready_for_review` (§3.1), so `POST /v1/onboarding/go-live` answered
// 422 PRECONDITIONS_UNMET forever. The wizard's only terminal outcome was
// unreachable by construction and §10's acceptance spine was dead. The step's
// completion is gated only on `vertical_pack_installed`, so completing it is
// legitimate today — what is deferred is the manual grids, not the step.
//
// What it genuinely offers now:
//  * an `as_of_date` — the business date the opening balances are "as of",
//    persisted in the step payload like every other step's scratch state;
//  * the five §3.5 import lanes over the SAME §3.3 pipeline every other lane
//    uses — `opening_stock`, `customers`, `suppliers`, `open_sales_orders`,
//    `open_purchase_orders`. Lanes whose owning module is unbound answer 422
//    IMPORT_KIND_NOT_AVAILABLE naming that module, which the shared import
//    problem notice already renders honestly (the same surface S3 shows).
//  * since P3-E2 PR E2-2c (D-325, spec 02 §4 S7) the `customers` lane is
//    BOUND: module 09's handler validates and commits it into the sales
//    customer book. The Customers and suppliers tab runs exactly the flow the
//    sales area's Import customers runs (spec 09 §4.18,
//    sales/screens/customers-import.tsx): the same upload panel, jobs table
//    and job page, so the two homes cannot drift. The customers lane's field
//    catalog (imports/import-model.ts) gives its mapping editor named fields,
//    and its row messages read in words. `suppliers` keeps its stated pending,
//    the 422 naming the module it waits on.
//  * the Open orders tab's `open_sales_orders` lane is bound too and runs the
//    flow of the sales area's Import orders (spec 09 §4.19,
//    sales/screens/orders-import.tsx). `open_purchase_orders` waits on
//    module 10.
//
// What it does NOT do, and says so: the manual opening-stock grid and the
// committed-count links wait on Modules 06/09/10. An honest "this lane is not
// bound yet, here is the module it waits on" is the whole point — the previous
// answer, silently having no screen, broke go-live instead.
//
// §7: each lane is gated by its own `onboarding.import.*` code (storekeeper gets
// opening_stock, sales customers, procurement suppliers), mirrored client-side
// and enforced server-side.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { CardSkeleton, ErrorState } from "../../../components/states";
import { Tabs } from "../../../components/tabs";
import { requestIdOf } from "../../../lib/request";
import { SaveContinueBar } from "../components/save-continue-bar";
import { StepContainer } from "../components/step-container";
import { useOnboardingSession } from "../hooks/onboarding-queries";
import { useOnboardingPermissions } from "../hooks/permissions";
import { useTerm } from "../hooks/terminology";
import { isWizardFrozen, stepStateOf } from "../lib/wizard-steps";
import { type ImportKind } from "./imports/import-model";
import { ImportJobsTable } from "./imports/jobs-table";
import { ImportUploadPanel } from "./imports/upload-panel";
import "./imports/imports.css";

type S = components["schemas"];

const STEP_NO = 4;

/** The three §4 S7 sub-tabs, each over the lanes that belong to it. */
type Tab = "stock" | "commercial" | "orders";

interface TabDef {
  readonly id: Tab;
  readonly labelKey: string;
  readonly kinds: readonly ImportKind[];
}

const STOCK_TAB: TabDef = {
  id: "stock",
  labelKey: "onboarding.opening.tabs.stock",
  kinds: ["opening_stock"],
};

const TABS: readonly TabDef[] = [
  STOCK_TAB,
  {
    id: "commercial",
    labelKey: "onboarding.opening.tabs.commercial",
    kinds: ["customers", "suppliers"],
  },
  {
    id: "orders",
    labelKey: "onboarding.opening.tabs.orders",
    kinds: ["open_sales_orders", "open_purchase_orders"],
  },
];

export function OpeningStepScreen() {
  const { t } = useTranslation();
  const { term } = useTerm();
  const perms = useOnboardingPermissions();
  const session = useOnboardingSession();

  const step = stepStateOf(session.data?.steps ?? [], STEP_NO);
  const frozen = session.data !== undefined && isWizardFrozen(session.data.session);

  const [tab, setTab] = useState<Tab>("stock");
  const [asOfDate, setAsOfDate] = useState("");
  const [seededFrom, setSeededFrom] = useState<string | null>(null);

  // Seed the scratch state once per loaded step version, and again after a 409
  // STALE_PAYLOAD hands us the server's copy (§3.1 "closing the browser loses
  // nothing").
  useEffect(() => {
    if (step === undefined || seededFrom === step.id) return;
    setSeededFrom(step.id);
    setTab(tabOf(step.payload));
    setAsOfDate(asOfDateOf(step.payload));
  }, [step, seededFrom]);

  function reseed(serverStep: S["OnboardingStepState"]) {
    setTab(tabOf(serverStep.payload));
    setAsOfDate(asOfDateOf(serverStep.payload));
  }

  if (session.isPending) {
    return (
      <StepContainer
        stepNo={STEP_NO}
        titleKey="onboarding.steps.opening.title"
        blurbKey="onboarding.steps.opening.blurb"
      >
        <CardSkeleton lines={5} label={t("onboarding.state.loading")} />
      </StepContainer>
    );
  }

  if (session.error !== null) {
    return (
      <StepContainer
        stepNo={STEP_NO}
        titleKey="onboarding.steps.opening.title"
        blurbKey="onboarding.steps.opening.blurb"
      >
        <ErrorState
          title={t("onboarding.state.errorTitle")}
          detail={t("onboarding.state.errorDetail")}
          retryLabel={t("onboarding.state.retry")}
          onRetry={() => void session.refetch()}
          requestId={requestIdOf(session.error)}
          requestIdLabel={t("onboarding.state.requestId")}
        />
      </StepContainer>
    );
  }

  const active: TabDef = TABS.find((entry) => entry.id === tab) ?? STOCK_TAB;
  const canEdit = perms.has("onboarding.manage_wizard") && !frozen;

  return (
    <StepContainer
      stepNo={STEP_NO}
      titleKey="onboarding.steps.opening.title"
      blurbKey="onboarding.steps.opening.blurb"
      footer={
        <SaveContinueBar
          stepNo={STEP_NO}
          getPayload={() => ({ tab, as_of_date: asOfDate })}
          updatedAt={step?.updated_at}
          // The DERIVED scratch, not the raw payload — same derivation the
          // seeding effect uses, so an untouched fresh step (`{}` payload)
          // reads "Saved", not "Unsaved changes" (§3.8).
          loadedPayload={
            step === undefined
              ? undefined
              : { tab: tabOf(step.payload), as_of_date: asOfDateOf(step.payload) }
          }
          onStale={reseed}
          continueTo="/onboarding/checklist"
          frozen={frozen}
        />
      }
    >
      <p className="wizard-step-blurb">
        {t("onboarding.opening.intro", { openingStock: term("opening_stock") })}
      </p>

      {/* Honest scope, stated before anything else on the screen. */}
      <div className="state state-empty wizard-notice" role="status" data-testid="opening-scope">
        <p className="state-title">{t("onboarding.opening.scopeTitle")}</p>
        <p className="state-body">{t("onboarding.opening.scopeBody")}</p>
      </div>

      <div className="ds-field opening-as-of">
        <label htmlFor="opening-as-of">{t("onboarding.opening.asOfLabel")}</label>
        <input
          id="opening-as-of"
          className="ds-input"
          type="date"
          value={asOfDate}
          readOnly={!canEdit}
          onChange={(e) => {
            setAsOfDate(e.target.value);
          }}
        />
        <p className="wizard-hint">{t("onboarding.opening.asOfHint")}</p>
      </div>

      <Tabs
        label={t("onboarding.opening.tabs.label")}
        tabs={TABS.map((entry) => ({
          id: entry.id,
          domId: `opening-tab-${entry.id}`,
          label: t(entry.labelKey),
        }))}
        active={tab}
        onSelect={(id) => {
          setTab(id === "commercial" || id === "orders" ? id : "stock");
        }}
        controls={`opening-panel-${active.id}`}
      />

      <section
        className="catalogue-import"
        role="tabpanel"
        id={`opening-panel-${active.id}`}
        aria-labelledby={`opening-tab-${active.id}`}
      >
        <ImportUploadPanel
          kinds={active.kinds}
          canImport={(kind: ImportKind) => perms.canImport(kind)}
        />
        <ImportJobsTable
          kinds={active.kinds}
          canRun={(kind: ImportKind) => perms.canImport(kind)}
        />
      </section>
    </StepContainer>
  );
}

/** Read the saved tab out of the step's free-form payload without trusting it. */
function tabOf(payload: Record<string, unknown> | undefined): Tab {
  const value = payload?.["tab"];
  return value === "commercial" || value === "orders" ? value : "stock";
}

/** `as_of_date` as an `<input type="date">` value, or empty. */
function asOfDateOf(payload: Record<string, unknown> | undefined): string {
  const value = payload?.["as_of_date"];
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";
}
